import { Drawer, Typography, Form, Radio, Tabs, Row, Col, Skeleton, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Button from '../Button/Button';
import { TimeSlotsByDate } from '../../redux/models/timeslots';
import { DeliveryOption } from '../../types';
import { TimeSlotWithDate } from '../../api/models/timeslot';
import { RadioChangeEvent } from 'antd/es/radio';
import { colorSuccessBg } from '../../styles/theme';
import { displayDeliveryTimeslotDate, displayDeliveryTimeslotTime } from '../../utils/componentHelpers';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import i18next from '../../i18next';
import Banner from '../Banner/Banner';

export type DeliveryOptionDrawerFormValues = {
  deliveryOption: DeliveryOption;
  deliverySlot: string;
};

type DeliveryOptionDrawerProps = {
  onClose: () => void;
  isVisible: boolean;
  isTimeSlotsFetching: boolean;
  hasDropoff: boolean;
  currentTimeSlots: TimeSlotsByDate;
  confirmDeliveryOption: (values: { deliverySlot?: TimeSlotWithDate; deliveryOption: DeliveryOption }) => void;
  fetchTimeSlots: () => Promise<void>;
  currentDeliveryOption?: DeliveryOption;
  currentDeliverySlot?: TimeSlotWithDate;
};

const skeletonRadioOptions = (
  <>
    <Skeleton style={{ marginTop: '16px' }} title loading active paragraph={{ rows: 10 }} />
  </>
);

const NoTimeSlotsBanner = () => (
  <Banner
    icon={<ExclamationCircleOutlined />}
    showIcon
    type={'info'}
    message={i18next.t('deliveries.deliveryOptionsDrawer.deliveryTimeSection.noSlots')}
  />
);

const DeliveryOptionDrawer = ({
  onClose,
  isVisible,
  confirmDeliveryOption,
  currentDeliveryOption,
  currentTimeSlots,
  isTimeSlotsFetching,
  fetchTimeSlots,
  hasDropoff,
  currentDeliverySlot
}: DeliveryOptionDrawerProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<DeliveryOptionDrawerFormValues>();

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values: DeliveryOptionDrawerFormValues) => {
        const { deliverySlot, deliveryOption } = values;

        const deliveryTimeSlotParsed: TimeSlotWithDate | undefined = deliverySlot ? JSON.parse(deliverySlot) : null;

        confirmDeliveryOption({
          deliveryOption,
          deliverySlot: deliveryTimeSlotParsed
        });
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
          screen_name: ANALYTIKA_SCREENS.SHEDULED_SIDEBAR_OPEN,
          event_version: 3,
          button_name: 'confirm',
          delivery_type: values.deliveryOption,
          delivery_date: deliveryTimeSlotParsed ? deliveryTimeSlotParsed.date : null,
          delivery_time_slot: deliveryTimeSlotParsed ? displayDeliveryTimeslotTime(deliveryTimeSlotParsed) : ''
        });
      })
      .catch(() => {
        // TODO: handle this in analytika
      });
  };

  const handleDeliveryOptionChange = (e: RadioChangeEvent) => {
    if (e.target.value === DeliveryOption.SCHEDULED) {
      fetchTimeSlots().then(() => {
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
          screen_name: ANALYTIKA_SCREENS.SHEDULED_SIDEBAR_OPEN,
          event_version: 3,
          button_name: DeliveryOption.SCHEDULED,
          available_slots: currentTimeSlots
        });
      });
    } else {
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_BUTTON,
        screen_name: ANALYTIKA_SCREENS.SHEDULED_SIDEBAR_OPEN,
        event_version: 3,
        button_name: DeliveryOption.INSTANT
      });
    }

    form.setFieldsValue({ deliverySlot: undefined });
  };

  const tabItems = useMemo(
    () =>
      currentTimeSlots && Object.keys(currentTimeSlots).length
        ? Object.keys(currentTimeSlots).map((slotDate, tabIndex) => {
            const curDateTimeslots = currentTimeSlots[slotDate];
            return {
              label: displayDeliveryTimeslotDate(slotDate),
              key: `sTab${tabIndex}`,
              children: (
                <Col style={{ display: 'flex', flexDirection: 'column', rowGap: '16px', marginTop: '16px' }}>
                  {curDateTimeslots && curDateTimeslots.length ? (
                    currentTimeSlots[slotDate].map(timeSlot => {
                      const { from, to } = timeSlot;
                      const radioValue = JSON.stringify({ date: slotDate, from, to });

                      return (
                        <Radio value={radioValue} key={radioValue}>
                          <Typography.Text>{`${displayDeliveryTimeslotTime({ to, from })}`}</Typography.Text>
                        </Radio>
                      );
                    })
                  ) : (
                    <NoTimeSlotsBanner />
                  )}
                </Col>
              )
            };
          })
        : [
            {
              label: i18next.t('deliveries.deliveryOptionsDrawer.deliveryTimeSection.today'),
              key: `sTabZero`,
              children: (
                <Col style={{ display: 'flex', flexDirection: 'column', rowGap: '16px', marginTop: '16px' }}>
                  <NoTimeSlotsBanner />{' '}
                </Col>
              )
            }
          ],
    [currentTimeSlots, isTimeSlotsFetching]
  );

  const selectedOption = Form.useWatch('deliveryOption', form);
  const selectedTimeSlot = Form.useWatch('deliverySlot', form);

  const isFormValid = selectedOption === DeliveryOption.INSTANT ? true : Boolean(selectedTimeSlot);

  return (
    <Drawer
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        padding: 0,
        borderBottom: 'none'
      }}
      footer={
        <Button block type="primary" disabled={!isFormValid} onClick={handleSubmit} data-testid="confirm-button">
          {t('deliveries.deliveryOptionsDrawer.confirm')}
        </Button>
      }
      title={
        <Typography.Title style={{ marginBottom: 0 }} level={2}>
          {t('deliveries.deliveryOptionsDrawer.title')}
        </Typography.Title>
      }
      onClose={onClose}
      open={isVisible}
    >
      <Form
        requiredMark={false}
        initialValues={{ deliveryOption: currentDeliveryOption, deliverySlot: JSON.stringify(currentDeliverySlot) }}
        layout="vertical"
        form={form}
      >
        <Form.Item
          name="deliveryOption"
          requiredMark={false}
          label={
            <Typography.Text style={{ padding: '16px' }} strong type="secondary">
              {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.title')}
            </Typography.Text>
          }
          rules={[{ required: true }]}
        >
          <Radio.Group style={{ display: 'flex', flexDirection: 'column' }} onChange={handleDeliveryOptionChange}>
            <Radio
              style={{
                padding: '0 16px',
                background: selectedOption === DeliveryOption.INSTANT ? colorSuccessBg : 'inherit'
              }}
              value={DeliveryOption.INSTANT}
            >
              <Col style={{ padding: '16px 8px' }}>
                <Row>
                  <Col>
                    <Typography.Title level={4} data-testid="instant-title">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.instant')}
                    </Typography.Title>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Typography.Text type="secondary" data-testid="instant-description">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.instantDescription')}
                    </Typography.Text>
                  </Col>
                </Row>
              </Col>
            </Radio>
            <Tooltip title={!hasDropoff ? t('deliveries.deliveryOptionsDrawer.tooltipDropoffRequired') : ''}>
              <Radio
                style={{
                  padding: '0 16px',
                  background: selectedOption === DeliveryOption.SCHEDULED ? colorSuccessBg : 'inherit'
                }}
                disabled={!hasDropoff}
                value={DeliveryOption.SCHEDULED}
              >
                <Col style={{ padding: '16px 8px' }}>
                  <Row>
                    <Col>
                      <Typography.Title disabled={!hasDropoff} level={4} data-testid="scheduled-title">
                        {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.scheduled')}
                      </Typography.Title>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Typography.Text disabled={!hasDropoff} type="secondary" data-testid="scheduled-description">
                        {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.scheduleDescription')}
                      </Typography.Text>
                    </Col>
                  </Row>
                </Col>
              </Radio>
            </Tooltip>
          </Radio.Group>
        </Form.Item>

        {selectedOption === DeliveryOption.SCHEDULED && (
          <Form.Item
            style={{ padding: '16px' }}
            name="deliverySlot"
            rules={[{ required: selectedOption === DeliveryOption.SCHEDULED, message: 'abc' }]}
            requiredMark={false}
            label={
              <Typography.Text strong type="secondary">
                {t('deliveries.deliveryOptionsDrawer.deliveryTimeSection.title')}
              </Typography.Text>
            }
          >
            {isTimeSlotsFetching ? (
              skeletonRadioOptions
            ) : (
              <Radio.Group>
                <Tabs type="card" style={{ marginTop: '16px' }} items={tabItems} />
              </Radio.Group>
            )}
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};

export default DeliveryOptionDrawer;
